import type { ComponentChildren } from 'preact';
import { createContext } from 'preact';
import { useContext } from 'preact/hooks';

import { CountryCodeSelectorContext } from '@/context/CountryCodeSelectorContext';
import { useWidgetConfig } from '@/context/WidgetConfigContext';
import { COLORS } from '@/helpers/constants/theme';
import { useHoverBackground } from '@/hooks/useHoverBackground';
import {
  useShopLoyaltyConfig,
  useUnregisteredCustomerForm,
} from '@/views/UnregisteredCustomer/hooks';
import type { I_UnregisteredCustomerContextValue } from '@/views/UnregisteredCustomer/types';

export const UnregisteredCustomerContext = createContext<I_UnregisteredCustomerContextValue | null>(
  null,
);

export function useUnregisteredCustomerContext() {
  const ctx = useContext(UnregisteredCustomerContext);
  if (!ctx) throw new Error('UnregisteredCustomerContext not found');
  return ctx;
}

export function UnregisteredCustomerProvider({ children }: { children: ComponentChildren }) {
  const { selectedCountry } = useContext(CountryCodeSelectorContext);

  const {
    countryCode: savedCountryCode,
    firstName: savedFirstName,
    lastName: savedLastName,
    phoneNumber: savedPhoneNumber,
    theme,
  } = useWidgetConfig();

  const { backgroundColor = COLORS.BRAND_PRIMARY, textColor = COLORS.TEXT_INVERSE } =
    theme?.primaryButton ?? {};

  const { backgroundColor: hoverBackground, hoverHandlers } = useHoverBackground({
    baseColor: backgroundColor,
  });

  const { loyaltyPrograms, loyaltyConfig, brandId, loading } = useShopLoyaltyConfig();
  const form = useUnregisteredCustomerForm();

  const value: I_UnregisteredCustomerContextValue = {
    ...form,
    loyaltyPrograms,
    loyaltyConfig,
    brandId,
    loading,
    savedCountryCode,
    savedFirstName,
    savedLastName,
    savedPhoneNumber,
    theme,
    hoverBackground,
    hoverHandlers,
    textColor,
    selectedCountry,
  };

  return (
    <UnregisteredCustomerContext.Provider value={value}>
      {children}
    </UnregisteredCustomerContext.Provider>
  );
}
